import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { api, fmtErr } from "../api";
import { useAuth } from "../auth";
import { Field } from "../components/bits";

// Simple single-column sign-in over the brand film (≈50% opacity), logo top-right. Email or username.
export default function Login() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/auth/login", form);
      const me = await api.get("/auth/me");
      setUser(me.data);
      nav("/");
    } catch (err) { setError(fmtErr(err)); } finally { setBusy(false); }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#0A0A0B] text-white" data-testid="login-page">
      <video className="absolute inset-0 h-full w-full object-cover opacity-50" autoPlay muted loop playsInline poster="/video/login.jpg" aria-hidden data-testid="login-video">
        <source src="/video/login.webm" type="video/webm" />
        <source src="/video/login.mp4" type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0B]/40 via-transparent to-[#0A0A0B]/70" aria-hidden />
      <header className="relative z-10 flex items-center justify-between px-6 py-5 sm:px-10">
        <Link to="/" className="text-[12px] font-medium text-zinc-300 hover:text-white" data-testid="auth-back-home">← traide.io</Link>
        <Link to="/" data-testid="auth-logo"><img src="/logo.webp" alt="TRAIDE" className="h-9 w-auto sm:h-10" /></Link>
      </header>
      <main className="relative z-10 flex min-h-[calc(100vh-72px)] items-center justify-center px-4 pb-16">
        <div className="w-full max-w-[400px] animate-in">
          <div className="mb-6 text-center">
            <div className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.18em] text-amber-500">The operating system for trade businesses</div>
            <h1 className="display mt-3 text-3xl font-extrabold tracking-tight sm:text-4xl">Welcome back</h1>
            <p className="mt-1.5 text-sm text-zinc-300">Sign in to your TRAIDE workspace.</p>
          </div>
          <form onSubmit={submit} className="glass space-y-4 rounded-xl border border-white/15 bg-white/[.08] p-6 text-ink shadow-2xl backdrop-blur-xl" data-testid="login-form">
            <Field label={<span className="text-zinc-200">Email or username</span>}><input data-testid="login-email" className="input !border-white/15 !bg-white/90" type="text" autoComplete="username" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" /></Field>
            <Field label={<span className="text-zinc-200">Password</span>}><input data-testid="login-password" className="input !border-white/15 !bg-white/90" type="password" autoComplete="current-password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" /></Field>
            {error && <div data-testid="login-error" className="rounded-md border border-rose-300/60 bg-rose-500/20 px-3 py-2 text-sm text-rose-100">{error}</div>}
            <button data-testid="login-submit" className="btn-amber touch w-full justify-center !min-h-[46px] !text-[14px]" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</button>
            <div className="text-center text-sm"><Link to="/forgot-password" data-testid="forgot-password-link" className="text-zinc-300 hover:text-white hover:underline">Forgot password?</Link></div>
          </form>
          <div className="mt-5 text-center text-sm text-zinc-300">New here? <Link to="/book-demo" data-testid="register-link" className="font-semibold text-white hover:underline">Book a demo</Link></div>
          <div className="mt-8 flex items-center justify-center gap-4 text-[12px] text-zinc-400"><Link to="/contact" className="hover:text-white hover:underline" data-testid="login-speak-link">Speak with TRAIDE</Link><Link to="/pricing" className="hover:text-white hover:underline">Pricing</Link><span className="flex items-center gap-1.5"><ShieldCheck size={13} className="text-emerald-400" /> Secure sign-in</span></div>
        </div>
      </main>
    </div>
  );
}
